import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session, User } from '@supabase/supabase-js';

export type AppRole = 'spaza' | 'brand' | 'community' | 'logistics' | 'admin' | 'data_capturer';

export interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  company: string | null;
  role: AppRole;
  operating_regions: string[];
  selected_products: string[];
  selected_brands: string[];
  onboarding_completed: boolean;
  is_admin?: boolean;
  is_deactivated?: boolean;
  deactivated_reason?: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) {
      if ((data as any).is_deactivated) {
        // Account deactivated by an admin — force sign-out.
        const reason = (data as any).deactivated_reason as string | null;
        await supabase.auth.signOut();
        setProfile(null);
        setSession(null);
        if (typeof window !== 'undefined') {
          const msg = reason
            ? `Your account has been temporarily deactivated: ${reason}`
            : 'Your account has been temporarily deactivated. Please contact support.';
          // Lazy import to avoid SSR issues
          import('sonner').then(({ toast }) => toast.error(msg)).catch(() => alert(msg));
        }
        return;
      }
      setProfile(data as Profile);
    }
  };

  const refreshProfile = async () => {
    if (session?.user?.id) {
      await fetchProfile(session.user.id);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;
      setSession(session);
      if (session?.user) {
        // Use setTimeout to avoid Supabase auth deadlock, but keep loading true
        setTimeout(async () => {
          await fetchProfile(session.user.id);
          if (isMounted) setLoading(false);
        }, 0);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      setSession(session);
      if (session?.user) {
        fetchProfile(session.user.id).then(() => {
          if (isMounted) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // Heartbeat: update profiles.last_seen_at every 60s while tab is visible
    const touch = () => {
      if (document.visibilityState === 'visible') {
        (supabase as any).rpc('touch_last_seen').then(() => {});
      }
    };
    touch();
    const interval = setInterval(touch, 60_000);
    const onVis = () => touch();
    document.addEventListener('visibilitychange', onVis);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);


  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, profile, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
